import { formatAmount } from "@/utils";
import { formatAxisAmount } from "@/utils/charts";
import {
  HorizontalAlignmentType,
  VerticalAlignmentType,
} from "recharts/types/component/DefaultLegendContent";

export type ChartType = "line" | "bar" | "pie";

export interface ISimpleBaseChart {
  type: ChartType;
  data: Array<{
    [key: string]: unknown;
  }>;
  id?: string;
  title?: string;
  description?: string;
  dataKeys?: string[];
  colors?: string[];
  labels?: {
    [key: string]: string;
  };
  className?: string;
  trend?: {
    direction: "up" | "down";
    text: string;
  };
  options?: ISimpleChartOptions;
  compact?: boolean;
}

export interface ISimpleLineChart extends ISimpleBaseChart {
  type: "line";
  xAxisKey?: string;
  yAxisKey?: string;
}

export interface ISimpleBarChart extends ISimpleBaseChart {
  type: "bar";
  xAxisKey?: string;
  yAxisKey?: string;
  horizontal?: boolean;
  stacked?: boolean;
}

export interface ISimplePieChart extends ISimpleBaseChart {
  type: "pie";
  nameKey?: string;
  valueKey?: string;
  innerRadius?: number;
  outerRadius?: number;
  legendVerticalAlign?: VerticalAlignmentType;
  legendHorizontalAlign?: HorizontalAlignmentType;
  legendLayout?: 'horizontal' | 'vertical';
}

export type SimpleChartData =
  | ISimpleLineChart
  | ISimpleBarChart
  | ISimplePieChart;

// export interface ChartConfig {
//   [key: string]: {
//     label?: React.ReactNode;
//     icon?: React.ComponentType;
//     color?: string;
//   };
// }

interface SimpleChartDefaults {
  colors: string[];
  xAxisKey: string;
  innerRadius: string;
  outerRadius: string;
  nameKey: string;
  valueKey: string;
  options: ISimpleChartOptions;
}

export interface ISimpleChartOptions {
  showLegend?: boolean;
  formatTooltipValue?: boolean;
  showGrid?: boolean;
  showDots?: boolean;
  dot?: React.ReactElement;
  activeDot?: React.ReactElement;
  showArea?: boolean;
  areaGradient?: boolean;
  areaOpacityFrom?: number; // 0..1
  areaOpacityTo?: number; // 0..1
  showLabels?: boolean;
  minHeight?: number;
  maxHeight?: number;
  barSize?: number;
  valueKey?: string;
  nameKey?: string;
  tooltipValueFormatter?: ((value: number) => string | React.ReactNode) | null;
  yAxisTickFormatter?: ((value: unknown, index: number) => string) | undefined;
  yAxisWidth?: number;
  wrapXAxisLabels?: boolean;
  maxCharsPerLine?: number;
  maxLines?: number;
  strokeWidth?: number;
}

export const chartDefaults: SimpleChartDefaults = {
  xAxisKey: "name",
  innerRadius: "0%",
  outerRadius: "100%",
  nameKey: "name",
  valueKey: "value",
  colors: [
    "hsl(var(--chart-1))",
    "hsl(var(--chart-2))",
    "hsl(var(--chart-3))",
    "hsl(var(--chart-4))",
    "hsl(var(--chart-5))",
    "hsl(var(--chart-blue))", // blue
    "hsl(var(--chart-red))", // red
    "hsl(var(--chart-green))", // green
    "hsl(var(--chart-yellow))", // yellow
    "hsl(var(--chart-purple))", // purple
    "hsl(var(--chart-6))",
    "hsl(var(--chart-7))",
    "hsl(var(--chart-8))",
    "hsl(var(--chart-9))",
    "hsl(var(--chart-10))",
  ],
  options: {
    formatTooltipValue: true,
    showLegend: true,
    showArea: false,
    showGrid: true,
    showDots: false,
    showLabels: true,
    minHeight: 200,
    maxHeight: 300,
    barSize: 30,
    nameKey: "name",
    valueKey: "value",
    tooltipValueFormatter: (value: number) => formatAmount(Number(value)),
    yAxisTickFormatter: (value: unknown) => formatAxisAmount(Number(value)),
    yAxisWidth: 80,
  },
};
